/**
 * Picks the layout for the current route.
 *
 * The B2C redirect callback renders bare (a spinner while `SsoAuthHandler` completes); every other
 * route renders inside `CortexShellSurface`, the CoOrdio application frame.
 *
 * WHY the old `VerticalLayout` stack is gone: `LayoutWrapper` + `VerticalLayout` + `Navbar` +
 * `Footer` + `VerticalNav` were a hand-built copy of a shell that `@coordio/ui` now supplies, and
 * keeping both meant Cortex's chrome would drift from CoordioCore's with every upstream change.
 * The replacement is one component, so a change to the shared shell lands here automatically.
 *
 * The footer is not carried over: `AppShell` locks the frame to the viewport and scrolls only
 * `<main>`, so a page-level footer would either sit inside the scroll area or fight the layout.
 */

import { useLocation } from 'react-router-dom';

import { CortexShellSurface } from '../../shell/CortexShellSurface';
import { callbackPath } from '../../config/authConfig';
import { ModuleGuard } from '../common/ModuleGuard';

import type { ReactNode } from 'react';

interface ConditionalLayoutProps {
  children: ReactNode;
}

export const ConditionalLayout = ({ children }: ConditionalLayoutProps) => {
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, '') || '/';
  const callbackPathNorm = callbackPath.replace(/\/$/, '') || '/';

  if (path === callbackPathNorm) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Redirects away from a route whose feature module is disabled. */}
      <ModuleGuard />
      <CortexShellSurface>{children}</CortexShellSurface>
    </>
  );
};
